import React, { useState, useEffect, useCallback } from 'react';
import {
  Search,
  Filter,
  Download,
  RefreshCw,
  Clock,
  AlertTriangle,
  FileText,
  ChevronLeft,
  ChevronRight,
  Loader2,
  X,
  Globe,
  Smartphone
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { API_BASE_URL } from '../constants';

interface LogEntry {
  timestamp: string;
  api_key: string;
  owner_id: string;
  path: string;
  method: string;
  status_code: number;
  processing_time_ms: number;
  ip_address: string;
  user_agent: string;
  error?: string;
  billing_event?: {
    transaction_type: string;
    amount_usd: number;
    contact_count: number;
  };
}

interface LogsResponse {
  logs: LogEntry[];
  total: number;
  page: number;
  size: number;
  total_pages: number;
}

interface LogFilters {
  startDate: string;
  endDate: string;
  apiKey: string;
  path: string;
  method: string;
  statusCode: string;
  ip_address: string;
}

const LogsViewer: React.FC = () => {
  const { apiKey } = useAuth();
  
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showFilters, setShowFilters] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  
  const [filters, setFilters] = useState<LogFilters>({
    startDate: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 24h ago
    endDate: new Date().toISOString().split('T')[0],
    apiKey: '',
    path: '',
    method: '',
    statusCode: '',
    ip_address: ''
  });

  const pageSize = 50;

  const buildURL = useCallback((forExport: boolean = false) => {
    const url = new URL(`${API_BASE_URL}/api/admin/logs/search`);
    url.searchParams.append('page', forExport ? '1' : String(page));
    url.searchParams.append('size', forExport ? '10000' : String(pageSize));
    
    if (filters.startDate) url.searchParams.append('start_date', `${filters.startDate}T00:00:00Z`);
    if (filters.endDate) url.searchParams.append('end_date', `${filters.endDate}T23:59:59Z`);
    if (filters.apiKey) url.searchParams.append('api_key', filters.apiKey);
    if (filters.path) url.searchParams.append('path', filters.path);
    if (filters.method) url.searchParams.append('method', filters.method);
    if (filters.statusCode) url.searchParams.append('status_code', filters.statusCode);
    if (filters.ip_address) url.searchParams.append('ip_address', filters.ip_address);
    
    return url;
  }, [filters, page, pageSize]);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const url = buildURL();
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${apiKey}` }
      });

      if (response.ok) {
        const data: LogsResponse = await response.json();
        setLogs(data.logs || []);
        setTotal(data.total || 0);
        setTotalPages(data.total_pages || 1);
      } else {
        setLogs([]);
        setTotal(0);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Failed to load logs:', error);
      setLogs([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [apiKey, buildURL]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handleFilterChange = useCallback((field: keyof LogFilters, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleSearch = useCallback(() => {
    setPage(1);
    loadLogs();
  }, [loadLogs]);

  const clearFilters = useCallback(() => {
    setFilters({
      startDate: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0],
      apiKey: '',
      path: '',
      method: '',
      statusCode: '',
      ip_address: ''
    });
    setPage(1);
  }, []);

  const exportLogs = useCallback(async () => {
    setExportLoading(true);
    try {
      const url = buildURL(true);
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${apiKey}` }
      });

      if (response.ok) {
        const data: LogsResponse = await response.json();
        const csv = convertToCSV(data.logs);
        downloadCSV(csv, `api-logs-${new Date().toISOString().split('T')[0]}.csv`);
      }
    } catch (error) {
      console.error('Failed to export logs:', error);
    } finally {
      setExportLoading(false);
    }
  }, [apiKey, buildURL]);

  const convertToCSV = (logsData: LogEntry[]): string => {
    const headers = ['Timestamp', 'API Key', 'Path', 'Method', 'Status', 'Response Time (ms)', 'IP Address', 'User Agent', 'Error', 'Billing Event'];
    const rows = logsData.map(log => [
      log.timestamp, log.api_key, log.path, log.method, log.status_code.toString(), log.processing_time_ms.toString(),
      log.ip_address, `"${log.user_agent.replace(/"/g, '""')}"`, log.error || '',
      log.billing_event ? `${log.billing_event.transaction_type}: $${log.billing_event.amount_usd}` : ''
    ]);
    return [headers, ...rows].map(row => row.join(',')).join('\n');
  };

  const downloadCSV = (csv: string, filename: string) => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatTimestamp = (timestamp: string) => new Date(timestamp).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const getStatusBadgeClass = (statusCode: number) => {
    if (statusCode >= 500) return 'status-badge critical';
    if (statusCode >= 400) return 'status-badge error';
    if (statusCode >= 300) return 'status-badge warning';
    return 'status-badge success';
  };
  const getMethodBadgeClass = (method: string) => `method-badge ${method.toLowerCase()}`;

  return (
    <div className="logs-viewer">
      <div className="card">
        <div className="card-header">
          <h2 className="section-title"><FileText size={20} />API Request Logs</h2>
          <div className="logs-controls">
            <button onClick={() => setShowFilters(!showFilters)} className={`btn btn-secondary btn-sm ${showFilters ? 'active' : ''}`}><Filter size={16} /> Filters</button>
            <button onClick={exportLogs} disabled={exportLoading} className="btn btn-secondary btn-sm">{exportLoading ? <Loader2 size={16} className="loading-spinner" /> : <Download size={16} />} Export CSV</button>
            <button onClick={loadLogs} disabled={loading} className="btn btn-secondary btn-sm">{loading ? <Loader2 size={16} className="loading-spinner" /> : <RefreshCw size={16} />} Refresh</button>
          </div>
        </div>

        {showFilters && (
          <div className="card-section border-bottom">
            <div className="logs-filters">
              <div className="filter-row">
                <div className="form-group"><label className="form-label">Start Date</label><input type="date" value={filters.startDate} onChange={(e) => handleFilterChange('startDate', e.target.value)} className="form-input" /></div>
                <div className="form-group"><label className="form-label">End Date</label><input type="date" value={filters.endDate} onChange={(e) => handleFilterChange('endDate', e.target.value)} className="form-input" /></div>
                <div className="form-group"><label className="form-label">API Path</label><input type="text" value={filters.path} onChange={(e) => handleFilterChange('path', e.target.value)} placeholder="/api/search" className="form-input" /></div>
                <div className="form-group"><label className="form-label">Method</label><select value={filters.method} onChange={(e) => handleFilterChange('method', e.target.value)} className="form-input"><option value="">All</option><option value="GET">GET</option><option value="POST">POST</option><option value="PUT">PUT</option><option value="DELETE">DELETE</option></select></div>
              </div>
              <div className="filter-row">
                <div className="form-group"><label className="form-label">Status Code</label><input type="text" value={filters.statusCode} onChange={(e) => handleFilterChange('statusCode', e.target.value)} placeholder="200, 404..." className="form-input" /></div>
                <div className="form-group"><label className="form-label">API Key</label><input type="text" value={filters.apiKey} onChange={(e) => handleFilterChange('apiKey', e.target.value)} placeholder="Filter by API key..." className="form-input" /></div>
                <div className="form-group"><label className="form-label">IP Address</label><input type="text" value={filters.ip_address} onChange={(e) => handleFilterChange('ip_address', e.target.value)} placeholder="Filter by IP..." className="form-input" /></div>
                <div className="form-group filter-actions"><button onClick={handleSearch} className="btn btn-primary"><Search size={16} /> Search</button><button onClick={clearFilters} className="btn btn-secondary"><X size={16} /> Clear</button></div>
              </div>
            </div>
          </div>
        )}

        <div className="card-body" style={{ padding: 0 }}>
          {logs.length === 0 && !loading ? (
            <div className="empty-section"><FileText size={48} /><h4>No Logs Found</h4><p>No API requests match your current filters.</p></div>
          ) : (
            <div className="table-wrapper">
              <table className="table-premium logs-table">
                <thead><tr><th>Timestamp</th><th>API Key</th><th>Method</th><th>Path</th><th>Status</th><th>Time</th><th>IP</th><th>Details</th></tr></thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 10 }).map((_, i) => (<tr key={i} className="skeleton-row"><td><div className="skeleton-text" /></td><td><div className="skeleton-text" /></td><td><div className="skeleton-text" /></td><td><div className="skeleton-text" /></td><td><div className="skeleton-text" /></td><td><div className="skeleton-text" /></td><td><div className="skeleton-text" /></td><td><div className="skeleton-text" /></td></tr>))
                  ) : (
                    logs.map((log, index) => (
                      <tr key={index}>
                        <td className="log-timestamp"><Clock size={14} />{formatTimestamp(log.timestamp)}</td>
                        <td className="log-api-key"><code>{log.api_key.slice(0, 8)}...</code></td>
                        <td><span className={getMethodBadgeClass(log.method)}>{log.method}</span></td>
                        <td className="log-path"><Globe size={14} />{log.path}</td>
                        <td><span className={getStatusBadgeClass(log.status_code)}>{log.status_code}</span></td>
                        <td className="log-time">{log.processing_time_ms}ms</td>
                        <td className="log-ip"><Smartphone size={14} />{log.ip_address}</td>
                        <td className="log-details">
                          {log.error && (<span className="error-indicator" title={log.error}><AlertTriangle size={14} /></span>)}
                          {log.billing_event && (<span className="billing-indicator" title={`${log.billing_event.transaction_type}: $${log.billing_event.amount_usd}`}>💳</span>)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="card-footer">
            <div className="pagination-info">Showing {((page - 1) * pageSize) + 1}-{Math.min(page * pageSize, total)} of {total.toLocaleString()} logs</div>
            <div className="pagination-controls">
              <button className="btn btn-secondary" onClick={() => { setPage(page - 1); loadLogs(); }} disabled={page <= 1}><ChevronLeft size={16} /> Previous</button>
              <span className="pagination-current">Page {page} of {totalPages}</span>
              <button className="btn btn-secondary" onClick={() => { setPage(page + 1); loadLogs(); }} disabled={page >= totalPages}>Next <ChevronRight size={16} /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LogsViewer;